import {
  Button,
  InfluencerPayment,
  InfluencerTransaction,
  LogService,
  Modal,
  NFSImported,
  onLangChange,
  Selector,
  StringsHelper,
  ToastHelper,
  ZeCariocaService,
} from '@hub/shared'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './modal-nfe-connection.component.scoped.scss'
import './modal-nfe-connection.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean
  transactionObject: InfluencerTransaction | null
  closeModal?: () => any
  updateTransaction?: () => any
  setOpenModalSendNf?: () => any
}

export default ({ open, transactionObject, closeModal, updateTransaction, setOpenModalSendNf }: Props) => {
  const onLangChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const stringsHelper = useMemo(() => new StringsHelper(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  const zeCariocaService = useMemo(() => new ZeCariocaService(), [])

  const [t] = useTranslation('modalNfeConnection')

  const [lang, setLang] = useState<string>('en')
  const [width, setWidth] = useState(window?.innerWidth)
  const [height, setHeight] = useState(window?.innerHeight)
  const [state, setState] = useState<NFSImported | null>(null)
  const [isOthersNfs, setIsOthersNfs] = useState<'isOthersNfs' | 'isNfe'>('isNfe')
  const [loadingButton, setLoadingButton] = useState(false)

  logService.init('Modal Nfe Connection')

  const onCloseModal = useCallback(
    (update: boolean) => {
      setIsOthersNfs('isNfe')
      setState(null)
      if (closeModal && typeof closeModal === 'function') {
        closeModal()
      }
      if (update) {
        if (updateTransaction && typeof updateTransaction === 'function') {
          updateTransaction()
        }
      }
    },
    [closeModal, updateTransaction]
  )

  useLayoutEffect(() => {
    const updateWidth = () => {
      setWidth(window?.innerWidth)
    }
    const updateHeight = () => {
      setHeight(window?.innerHeight)
    }
    window.addEventListener('resize', updateWidth)
    window.addEventListener('resize', updateHeight)
    return () => {
      window.removeEventListener('resize', updateWidth)
      window.removeEventListener('resize', updateHeight)
    }
  }, [])

  useEffect(() => {
    onLangChangeSubscription.current = onLangChange.subscribe((lang) => {
      setLang(lang)
    })

    return () => {
      onLangChangeSubscription.current?.unsubscribe()
    }
  }, [])

  const renderCampaigns = useCallback((payments: InfluencerPayment[]) => {
    return payments?.map((payment: InfluencerPayment) => `${payment?.campaignName}`).join(', ')
  }, [])

  const submit = useCallback(() => {
    setLoadingButton(true)
    zeCariocaService
      .postImportedNfe(transactionObject!.transaction.transactionId, state?.id.toString() || '')
      .then((e) => {
        onCloseModal(true)
        toast.success(t('success'), {
          closeButton: true,
          className: 'full text-bold',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          notOverClick: true,
          immediately: true,
        })
      })
      .catch((error) => {
        logService.error({ message: 'Error on save nfe imported', error: error })
        toast.error(t('error'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        setLoadingButton(false)
      })
  }, [transactionObject, state, logService, onCloseModal, t, toast, zeCariocaService])

  const optionsButtonConfig = useMemo(
    () => ({
      isOthersNfs: {
        label: t('selectInvoices'),
        action: () => {
          onCloseModal(false)
          if (setOpenModalSendNf && typeof setOpenModalSendNf === 'function') {
            setOpenModalSendNf()
          }
        },
      },
      isNfe: {
        label: t('save'),
        action: () => submit(),
      },
    }),
    [t, setOpenModalSendNf, submit, onCloseModal]
  )

  const formatToCompare = (value: number) => {
    return value?.toString().replace(/\D/g, '')
  }

  const formatValue = (value?: number) => {
    return value?.toLocaleString('pt-br', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 2,
    })
  }

  return (
    <Modal
      className="modal-nfe-connection p-0"
      open={open}
      modalSize="md"
      forceMobileNoMargin
      onCloseChange={() => onCloseModal(false)}
      header={<h5 className="m-0">{t('title')}</h5>}
      footer={
        <div className="display-flex justify-content-end">
          <Button
            id="confirm-button-modal-nfe-connection"
            className="confirm-button-modal-nfe-connection"
            size="sm"
            onClick={() => {
              optionsButtonConfig?.[isOthersNfs]?.action()
            }}
            disabled={
              !state ||
              loadingButton ||
              (state?.id && formatToCompare(state?.valor_servico) !== formatToCompare(transactionObject!.transaction?.netValue)) ||
              false
            }
            loading={loadingButton}
          >
            <span>{optionsButtonConfig?.[isOthersNfs]?.label}</span>
          </Button>
        </div>
      }
    >
      <div className="modal-nfe-connection-content" style={{ height: width <= 991 || height < 771 ? `calc(${height}px - 12rem)` : 'auto' }}>
        <section>
          <h6 className="mb-3">{t('referenceMonth')}</h6>
          <div className="reference-month">
            <span>
              <strong>
                {stringsHelper.capitalizeFirstLetter(
                  new Date(transactionObject?.transaction?.dueDate)?.toLocaleString(lang, { month: 'long' })
                )}{' '}
                {transactionObject?.transaction?.dueDate?.substring(0, 4)}
              </strong>
              <p>
                {t('campaigns')}
                {transactionObject ? renderCampaigns(transactionObject?.payments) : null}
              </p>
            </span>
            <span className="value">
              <strong>{formatValue(transactionObject?.transaction?.netValue)}</strong>
            </span>
          </div>
        </section>
        <section className="my-5">
          <div className="nfs-title">
            <h6>{t('nfsTitle')}</h6>
            {/* 
              TODO: O Link ainda n existe, colocar o link no href quando houver
              
              <a href='' target="_blank">{t('nfsLink')}</a> 
            */}
          </div>
          <div
            className="nfs-radio scrollbar"
            style={width < 992 ? { height: `calc(${height}px - 465px)` } : { height: `calc(${height}px - ${(height / 100) * 65}px)` }}
          >
            {transactionObject?.nfsImported?.map((nfe: NFSImported, index: number) => (
              <Selector
                name="nfe-selector"
                id={`nfe-selector-${index}`}
                key={`nfe-selector-${nfe?.id}`}
                checked={state?.id === nfe?.id}
                onChange={(checked) => {
                  setState(nfe)
                  checked && setIsOthersNfs('isNfe')
                  if (formatToCompare(nfe?.valor_servico) !== formatToCompare(transactionObject?.transaction?.netValue)) {
                    toast.error(t('divergentValue'), {
                      closeButton: true,
                      className: 'full',
                      position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
                      duration: 3600000,
                      notOverClick: true,
                      immediately: true,
                    })
                  }
                }}
                errorSpan={false}
                type="radio"
                rightLabel={
                  <div className="nfe-selector-label">
                    <div>
                      <span className="mr-2">{`NF${nfe?.numero_nf}`}</span>
                      <a href={nfe?.nf_storage_tmp} target="_blank">
                        <i className="fa-light fa-eye" style={{ color: 'var(--blue)' }}></i>
                      </a>
                    </div>
                    <span>
                      <strong>{formatValue(nfe?.valor_servico)}</strong>
                    </span>
                  </div>
                }
              />
            ))}
            <Selector
              name="nfe-selector"
              id="nfe-selector-more-than-one"
              key="nfe-selector-more-than-one"
              checked={!!state && !state?.id}
              onChange={(checked) => {
                setState({} as NFSImported)
                checked && setIsOthersNfs('isOthersNfs')
              }}
              errorSpan={false}
              type="radio"
              rightLabel={
                <div className="nfe-selector-label">
                  <span>{t('moreThanOne')}</span>
                </div>
              }
            />
          </div>
        </section>
        <p>{t('nfsWarning')}</p>
      </div>
    </Modal>
  )
}
